"use client";
import Link from "next/link";
import { ArrowRight, BookOpen, BarChart3, Send, Activity, Siren, Globe } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { platformPreview as data } from "@/lib/data/demo";

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = { BookOpen, BarChart3, Send, Activity, Siren, Globe };
const accents = ["var(--color-crimson)", "var(--color-purple)", "var(--color-cyan)", "var(--color-amber)", "var(--color-rose)", "var(--color-emerald)"];

export function PlatformPreview() {
  return (
    <Section id="platform-preview" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.capabilities.map((c, i) => {
            const Icon = iconMap[c.icon] ?? BookOpen;
            const accent = accents[i % accents.length]!;
            return (
              <Reveal key={c.name} delay={i * 80}>
                <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-11 w-11 rounded-xl" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                      <Icon className="h-5 w-5" style={{ color: accent }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Capability {String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-4">{c.name}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
